export type PostAudience = 'SYNDICATE_USERS' | 'CLOSINGS' | 'INVITEES';

export const POST_AUDIENCE_OPTIONS: { label: string; value: PostAudience }[] = [
  { label: 'All LPs of this Syndicate', value: 'SYNDICATE_USERS' },
  { label: 'Only the investors in this deal', value: 'CLOSINGS' },
  { label: 'Investors invited to this deal (need not have invested)', value: 'INVITEES' },
];

export const DEFAULT_POST_AUDIENCE: PostAudience[] = ['SYNDICATE_USERS'];

type StoredAudience = {
  targetSyndicateUsers?: boolean | null;
  targetInvitees?: boolean | null;
  targetClosingStatuses?: readonly string[] | null;
};

// Posts saved before audiences were combinable have no flags; an empty audience meant "all LPs".
export function audienceFromPost(post: StoredAudience): PostAudience[] {
  const audience: PostAudience[] = [];
  if (post.targetSyndicateUsers) audience.push('SYNDICATE_USERS');
  if (post.targetClosingStatuses && post.targetClosingStatuses.length > 0)
    audience.push('CLOSINGS');
  if (post.targetInvitees) audience.push('INVITEES');
  return audience.length > 0 ? audience : DEFAULT_POST_AUDIENCE;
}

export function audienceToVariables<StageStatus>(
  audience: PostAudience[],
  targetClosingStatuses: StageStatus[] | undefined
) {
  return {
    targetSyndicateUsers: audience.includes('SYNDICATE_USERS'),
    targetInvitees: audience.includes('INVITEES'),
    targetClosingStatuses: audience.includes('CLOSINGS') ? targetClosingStatuses : undefined,
  };
}

export function isAudienceMissing(audience: PostAudience[] | undefined): boolean {
  return !audience || audience.length === 0;
}

export function isStageSelectionMissing(
  audience: PostAudience[],
  targetClosingStatuses: string[] | undefined
): boolean {
  return (
    audience.includes('CLOSINGS') && (!targetClosingStatuses || targetClosingStatuses.length === 0)
  );
}

const AUDIENCE_SHORT_LABELS: Record<PostAudience, string> = {
  SYNDICATE_USERS: 'All syndicate LPs',
  CLOSINGS: 'Investors in the deal',
  INVITEES: 'Invitees of the deal',
};

export function describeAudience(post: StoredAudience): string {
  return audienceFromPost(post)
    .map(group => {
      if (group !== 'CLOSINGS') return AUDIENCE_SHORT_LABELS[group];
      const stages = (post.targetClosingStatuses || []).map(status => status.toLowerCase());
      return `${AUDIENCE_SHORT_LABELS[group]} (${stages.join(', ')})`;
    })
    .join(' + ');
}
